import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import RetailersPageContainer from '../containers/pages/RetailersPageContainer';
import OrdersPageContainer from '../containers/pages/OrdersPageContainer';
import SignInContainer from '../containers/pages/SignInContainer';
import SingleRetailerPageContainer from '../containers/pages/SingleRetailerPageContainer';
import DropshipOrdersPageContainer from '../containers/pages/DropshipOrdersPageContainer';
import AccountPageContainer from '../containers/pages/AccountPageContainer';
import BrandPageContainer from '../containers/pages/BrandPageContainer';
import UsersPageContainer from '../containers/pages/UsersPageContainer';
import DropshipPageContainer from '../containers/pages/DropshipPageContainer';
import ReplenishProductsPageContainer from '../containers/pages/ReplenishProductsPageContainer';
import ResetPasswordContainer from '../containers/pages/ResetPasswordContainer';
import WelcomePageContainer from '../containers/pages/WelcomePageContainer';
import CreateNewPasswordContainer from '../containers/pages/CreateNewPasswordContainer';
import DropshipProductCardPageContainer from '../containers/pages/DropshipProductCardPageContainer';
import ActivationPageContainer from '../containers/pages/ActivationPageContainer';
import SignUpContainer from '../containers/pages/SignUpContainer';
import CreateNewRetailerAccountPage from '../containers/pages/CreateNewRetailerAccountPage';
import CartContainer from '../containers/pages/CartContainer';
import PermissionDeniedContainer from '../containers/pages/PermissionDeniedContainer';
import { ProductsReplenishPageContainer } from '../containers/pages/ProductsReplenishPageContainer';
import { ProductsDropshipPageContainer } from '../containers/pages/ProductsDropshipPageContainer';
import SupplierInventoryPageContainer from '../containers/pages/SupplierInventoryPageContainer';
import SupplierPermissionsRequiredHOC from '../hoc/SupplierPermissionsRequiredHOC';
import RetailerPermissionsRequiredHOC from '../hoc/RetailerPermissionsRequiredHOC';
import SupplierAdminUsersContainer from '../containers/pages/suppliers/AdminUsersContainer';
import SupplierAdminGeneralContainer from '../containers/pages/suppliers/AdminGeneralContainer';
import SupplierAdminWarehousesContainer from '../containers/pages/suppliers/AdminWarehousesContainer';
import NoMatchContainer from '../containers/pages/NoMatchContainer';
import SignInFlowContainer from '../containers/common/SignInFlowContainer';
import { ShopifyIntegrationConfirmPage } from '../containers/pages/retailers/IntegrationShopifyContainer';
import IntegrationEbayContainer from '../containers/pages/retailers/IntegrationEbayContainer';
import IntegrationGoogleContainer from '../containers/pages/retailers/IntegrationGoogleContainer';
import IntegrationShipBobContainer from '../containers/pages/suppliers/IntegrationShipBobContainer';
import SuppliersIntegrationShopifyContainer from '../containers/pages/suppliers/SuppliersIntegrationShopifyContainer';
import { SuppliersConnectChannelIntegration } from '../containers/pages/suppliers/SuppliersConnectChannelIntegration';
import UserRequiredHOC from '../hoc/UserRequiredHOC';
import UserAccountPageContainer from '../containers/pages/user/UserAccountPageContainer';
import UserNotificationsPageContainer from '../containers/pages/user/UserNotificationsPageContainer';
import SuppliersIntegrationAmazonMcfContainer from '../containers/pages/suppliers/SuppliersIntegrationAmazonMcfContainer';
import ShopifyRetailerAuthContainer from '../containers/pages/retailers/ShopifyRetailerAuthContainer';
import ShopifyMarketplaceAuthContainer from '../containers/pages/retailers/ShopifyMarketplaceAuthContainer';
import { IntegrationNetSuiteContainer } from '../containers/pages/suppliers/IntegrationNetSuiteContainer';
import { ConnectNetSuiteWarehouseContainer } from '../containers/pages/suppliers/ConnectNetSuiteWarehouseContainer';
import { ConnectNetSuiteWarehouseRedirect } from '../containers/pages/redirects/ConnectNetSuiteWarehouseRedirect';
import { WarehouseEditContainer } from '../containers/pages/warehouses/WarehouseEditContainer';
import { ConnectSalesforceWarehouseRedirect } from '../containers/pages/redirects/ConnectSalesforceWarehouseRedirect';
import { ConnectSalesforceWarehouseContainer } from '../containers/pages/suppliers/ConnectSalesforceWarehouseContainer';
import { SupplierPricingPage } from '../components/blocks/SupplierPricingPage/SupplierPricingPage';
import ProductsFeedsPageContainer from '../containers/pages/ProductsFeedsPageContainer';
import { DashboardPage } from '../components/pages/Analytics/DashboardPage';
import {
  Returns,
  SalesByProduct,
  SalesByWarehouse,
  SalesByChannel,
  Cancellations,
  Rlrs,
} from '../components/pages/Reports';
import { useProductsFeedPermissions } from '../hooks/useProductsFeedPermission';
import { ReportsManager } from '../components/pages/ReportsManager/ReportsManager';

function MainRoutes() {
  const { isProductsFeedEnabled } = useProductsFeedPermissions();

  return (
    <Switch>
      <Redirect
        exact
        from="/suppliers/:supplierId/products/dropship"
        to="/suppliers/:supplierId/products/catalogue-management"
      />
      <Redirect exact from="/retailers/:retailerId/dropship" to="/retailers/:retailerId/catalogue-management" />
      <Redirect
        exact
        from="/retailers/:retailerId/dropship/product/:model/:supplierId"
        to="/retailers/:retailerId/catalogue-management/product/:model/:supplierId"
      />
      <Route path="/welcome" component={WelcomePageContainer} />
      <Route exact path="/resetPassword" component={ResetPasswordContainer} />
      <Route path="/resetPassword/:token" component={CreateNewPasswordContainer} />
      <Redirect exact from="/forgot" to="/resetPassword" />
      <Route path="/signup" component={SignUpContainer} />
      <Route path="/signin" component={SignInContainer} />
      <Route exact path="/newretailer" component={CreateNewRetailerAccountPage} />
      <Route exact path="/shopify/retailers/auth" component={ShopifyRetailerAuthContainer} />
      <Route exact path="/shopify/retailers/marketplace-auth" component={ShopifyMarketplaceAuthContainer} />
      <Route exact path="/shopify/retailers/confirm" component={ShopifyIntegrationConfirmPage} />
      <Route exact path="/retailers/integrations/ebay" component={IntegrationEbayContainer} />
      <Route exact path="/retailers/integrations/google" component={IntegrationGoogleContainer} />
      <Route exact path="/integrations/shopify/warehouse" component={SuppliersIntegrationShopifyContainer} />
      <Route exact path="/integrations/amazon_mcf" component={SuppliersIntegrationAmazonMcfContainer} />
      <Route exact path="/integrations/shipbob" component={IntegrationShipBobContainer} />
      <Route exact path="/integrations/net_suite" component={IntegrationNetSuiteContainer} />
      {/* Supplier routes: */}
      <Route
        exact
        path="/suppliers/:supplierId/retailers"
        component={SupplierPermissionsRequiredHOC(RetailersPageContainer)}
      />
      <Route
        path="/suppliers/:supplierId/retailers/:retailerId"
        component={SupplierPermissionsRequiredHOC(SingleRetailerPageContainer)}
      />
      <Route path="/suppliers/:supplierId/orders" component={SupplierPermissionsRequiredHOC(OrdersPageContainer)} />
      {isProductsFeedEnabled && (
        <Route
          path="/suppliers/:supplierId/products/feeds"
          component={SupplierPermissionsRequiredHOC(ProductsFeedsPageContainer)}
        />
      )}
      <Route
        path="/suppliers/:supplierId/products/reorder"
        component={SupplierPermissionsRequiredHOC(ProductsReplenishPageContainer)}
      />
      <Route
        path="/suppliers/:supplierId/products/catalogue-management"
        component={SupplierPermissionsRequiredHOC(ProductsDropshipPageContainer)}
      />
      <Route
        path="/suppliers/:supplierId/inventory"
        component={SupplierPermissionsRequiredHOC(SupplierInventoryPageContainer)}
      />
      {/* Redirect to analytics for deprecated stats page */}
      <Redirect exact from="/suppliers/:supplierId/stats" to="/suppliers/:supplierId/analytics/dashboards/sales" />
      <Redirect exact from="/suppliers/:supplierId/analytics" to="/suppliers/:supplierId/analytics/dashboards/sales" />
      <Redirect
        exact
        from="/suppliers/:supplierId/analytics/sales"
        to="/suppliers/:supplierId/analytics/dashboards/sales"
      />
      <Route
        exact
        path="/suppliers/:supplierId/analytics/dashboards/sales"
        component={SupplierPermissionsRequiredHOC(DashboardPage)}
      />
      <Route
        path="/suppliers/:supplierId/analytics/reports/sales"
        component={SupplierPermissionsRequiredHOC(SalesByChannel)}
      />
      <Route
        path="/suppliers/:supplierId/analytics/reports/cancellations"
        component={SupplierPermissionsRequiredHOC(Cancellations)}
      />
      <Route
        path="/suppliers/:supplierId/analytics/reports/returns"
        component={SupplierPermissionsRequiredHOC(Returns)}
      />
      <Route
        path="/suppliers/:supplierId/analytics/reports/sales-by-product"
        component={SupplierPermissionsRequiredHOC(SalesByProduct)}
      />
      <Route
        path="/suppliers/:supplierId/analytics/reports/sales-by-warehouse"
        component={SupplierPermissionsRequiredHOC(SalesByWarehouse)}
      />
      <Route
        path="/suppliers/:supplierId/analytics/reports/return-notifications"
        component={SupplierPermissionsRequiredHOC(Rlrs)}
      />
      <Route
        exact
        path="/suppliers/:supplierId/analytics/reports"
        component={SupplierPermissionsRequiredHOC(ReportsManager)}
      />

      <Redirect exact from="/suppliers/:supplierId/admin" to="/suppliers/:supplierId/admin/general" />
      <Route
        exact
        path="/suppliers/:supplierId/admin/warehouses"
        component={SupplierPermissionsRequiredHOC(SupplierAdminWarehousesContainer, true)}
      />
      <Route
        exact
        path="/suppliers/:supplierId/admin/general"
        component={SupplierPermissionsRequiredHOC(SupplierAdminGeneralContainer, true)}
      />
      <Route
        exact
        path="/suppliers/:supplierId/admin/users"
        component={SupplierPermissionsRequiredHOC(SupplierAdminUsersContainer, true)}
      />
      <Route
        exact
        path="/suppliers/:supplierId/warehouses/net-suite"
        component={SupplierPermissionsRequiredHOC(ConnectNetSuiteWarehouseContainer, true)}
      />
      <Route
        exact
        path="/suppliers/:supplierId/warehouses/salesforce"
        component={SupplierPermissionsRequiredHOC(ConnectSalesforceWarehouseContainer, true)}
      />
      <Route
        exact
        path="/suppliers/:supplierId/warehouses/:warehouseId/edit"
        component={SupplierPermissionsRequiredHOC(WarehouseEditContainer)}
      />

      <Route exact path="/suppliers/warehouses/net-suite" component={ConnectNetSuiteWarehouseRedirect} />
      <Route exact path="/suppliers/warehouses/salesforce" component={ConnectSalesforceWarehouseRedirect} />
      <Redirect from="/suppliers/:supplierId/products/replenishment" to="/suppliers/:supplierId/products/reorder" />
      <Redirect exact from="/suppliers/:supplierId" to="/suppliers/:supplierId/retailers" />
      <Route exact path="/suppliers/channels/oauth" component={SuppliersConnectChannelIntegration} />

      <Route
        exact
        path="/suppliers/:supplierId/pricing"
        component={SupplierPermissionsRequiredHOC(SupplierPricingPage)}
      />

      {/* Retailer routes: */}
      <Route
        exact
        path="/retailers/:retailerId/catalogue-management"
        component={RetailerPermissionsRequiredHOC(DropshipPageContainer)}
      />
      <Route
        path="/retailers/:retailerId/dropship/orders"
        component={RetailerPermissionsRequiredHOC(DropshipOrdersPageContainer)}
      />
      <Route
        exact
        path="/retailers/:retailerId/catalogue-management/product/:model/:supplierId"
        component={RetailerPermissionsRequiredHOC(DropshipProductCardPageContainer)}
      />
      <Route
        exact
        path="/retailers/:retailerId/reorder"
        component={RetailerPermissionsRequiredHOC(ReplenishProductsPageContainer)}
      />
      <Route
        exact
        path="/retailers/:retailerId/reorder/cart"
        component={RetailerPermissionsRequiredHOC(CartContainer)}
      />
      <Redirect exact from="/retailers/:retailerId/reorder/orders" to="/retailers/:retailerId/dropship/orders" />
      <Route
        exact
        path="/retailers/:retailerId/reorder/products"
        component={RetailerPermissionsRequiredHOC(ReplenishProductsPageContainer)}
      />
      <Redirect exact from="/retailers/:retailerId/admin" to="/retailers/:retailerId/admin/account" />
      <Route
        exact
        path="/retailers/:retailerId/admin/activation/:supplierId"
        component={RetailerPermissionsRequiredHOC(ActivationPageContainer, true)}
      />
      <Route
        exact
        path="/retailers/:retailerId/admin/account"
        component={RetailerPermissionsRequiredHOC(AccountPageContainer, true)}
      />
      <Route
        exact
        path="/retailers/:retailerId/admin/brands"
        component={RetailerPermissionsRequiredHOC(BrandPageContainer, true)}
      />
      <Route
        exact
        path="/retailers/:retailerId/admin/users"
        component={RetailerPermissionsRequiredHOC(UsersPageContainer, true)}
      />
      <Route
        exact
        path="/retailers/:retailerId/no-suppliers"
        component={RetailerPermissionsRequiredHOC(PermissionDeniedContainer)}
      />
      <Redirect exact from="/retailers/:retailerId/replenish" to="/retailers/:retailerId/reorder" />
      <Redirect from="/retailers/:retailerId/replenish/cart" to="/retailers/:retailerId/reorder/cart" />
      <Redirect from="/retailers/:retailerId/replenish/orders" to="/retailers/:retailerId/reorder/orders" />
      <Redirect from="/retailers/:retailerId/replenish/products" to="/retailers/:retailerId/reorder/products" />
      <Redirect exact from="/retailers/:retailerId" to="/retailers/:retailerId/catalogue-management" />
      {/* User routes */}
      <Redirect exact from="/user" to="/user/account" />
      <Route exact path="/user/account" component={UserRequiredHOC(UserAccountPageContainer)} />
      <Route exact path="/user/notifications" component={UserRequiredHOC(UserNotificationsPageContainer)} />
      <Route exact path="/" component={SignInFlowContainer} />

      {/* Not found */}
      <Route path="*" component={NoMatchContainer} status={404} />
    </Switch>
  );
}

export default MainRoutes;
